
import React from "react";
import { Route, Routes, Outlet, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import Header from "parts/Header";
import Footer from "parts/Footer";
import UniversalHero from "parts/UniversalHero";

import LandingPage from "pages/LandingPage";
import NotFoundPage from "pages/NotFoundPage";
import TeamPage from "pages/TeamPage";
// contact
import Contact from 'pages/contact';

// services_pages
import ServicesPage from "pages/services/ServicesPage";
import CloudSolutions from "pages/services/Cloud_Solutions";
import ManagedITServices from "pages/services/Managed_IT_Services";
import CybersecurityServices from "pages/services/Cybersecurity_Services";
import HardwareAsAService from "pages/services/Hardware-as-a-Service";
import DisasterRecovery from "pages/services/Disaster_recovery";
import ITConsulting from "pages/services/IT_Consulting";
import VCioServices from "pages/services/vCIO_Services";
import NetworkSecurity from "pages/services/Network_Security";
import HostedPrivateCloud from "pages/services/Hosted_Private_Cloud";
import ManagedPrintServices from "pages/services/Managed_Print_Services";
import RoboticProcessAutomation from "pages/services/Robotic_Process_Automation";
import ServiceDetailPage from "pages/services/ServicesDetailPage";

// Industries_pages
import IndustriesPage from "pages/Industries/Industries";
import IndustryDetailPage from "pages/Industries/IndustryDetailPage";
import Audiology from "pages/Industries/Audiology";
import Automotive from "pages/Industries/Automotive";
import Aviation from "pages/Industries/Aviation";
import EducationTraining from "pages/Industries/education-and-training-institutions";
import FinancialServices from "pages/Industries/Financial_Services_and_Accounting_Firms";
import HealthcareMedical from "pages/Industries/Healthcare_and_Medical_Practices";
import HospitalityTourism from "pages/Industries/Hospitality_and_Tourism";
import LegalFirms from "pages/Industries/legal_Firms";
import LocalGovMunicipal from "pages/Industries/Local_Governments_and_Municipalities";
import ManufacturingLogistics from "pages/Industries/Manufacturing_and_Logistics";
import NonprofitOrganizations from "pages/Industries/Nonprofit_Organizations";
import ProfessionalServices from "pages/Industries/professional-services-consulting-marketing-hr";
import RealEstateProperty from "pages/Industries/Real_Estate_and_Property_Management";
import RetailEcommerce from "pages/Industries/Retail_and_Ecommerce";

// about_pages
import About from 'pages/about/about';
import OurMissionAndValues from "pages/about/Our_Mission_And_Values";
import AwardsAndRecognition from "pages/about/Awards_And_Recognition";
import EWasteRecycling from "pages/about/E_Waste_Recycling";
import EventsHub from "pages/about/Events_Hub";
import FAQs from "pages/about/FAQs";

/* ---------- Booking pages ---------- */
import BookConsultation from "pages/Book_Consultation";
import BookConsultationThanks from "pages/book-consultation/Thanks";

/* ---------- Admin CMS Integration ---------- */
import { AuthProvider } from "./admin/context/AuthContext";
import { ThemeProvider } from "./admin/context/ThemeContext";
import AdminLayout from "./admin/components/AdminLayout";
import RequireAuth from "./admin/components/RequireAuth";

import Login from "./admin/pages/Login";
import InstallPage from "./admin/pages/InstallPage";
import ForgotPassword from "./admin/pages/ForgotPassword";
import ResetPassword from "./admin/pages/ResetPassword";
import Dashboard from "./admin/pages/Dashboard";
import PageManager from "./admin/pages/PageManager";
import PageBuilder from "./admin/pages/PageBuilder";
import ServicesManager from "./admin/pages/ServicesManager";
import IndustriesManager from "./admin/pages/IndustriesManager";
import PortfolioManager from "./admin/pages/PortfolioManager";
import TeamManager from "./admin/pages/TeamManager";
import TestimonialsManager from "./admin/pages/TestimonialsManager";
import FaqManager from "./admin/pages/FaqManager";
import MediaLibrary from "./admin/pages/MediaLibrary";
import BlogManager from "./admin/pages/BlogManager";
import BookingsManager from "./admin/pages/BookingsManager";
import MessagesManager from "./admin/pages/MessagesManager";
import SettingsPage from "./admin/pages/SettingsPage";
import AnalyticsDashboard from "./admin/pages/AnalyticsDashboard";
import AiAssistantPage from "./admin/pages/AiAssistantPage";
import UserManagement from "./admin/pages/UserManagement";
import AuditLogsPage from "./admin/pages/AuditLogsPage";

import "./assets/css/styles.css";

import { Toaster } from "react-hot-toast";

/* Layout that ensures Header -> UniversalHero -> Outlet -> Footer */
function Layout() {
  return (
    <>
      <Toaster position="top-right" toastOptions={{ duration: 4000 }} />
      <Header />
      <UniversalHero />
      <main className="relative z-0">
        <Outlet />
      </main>
      <Footer />
    </>
  );
}

function App() {
  const isAdminHost = typeof window !== 'undefined' && (
    window.location.hostname === 'admin.fajtech.ca' ||
    window.location.hostname.startsWith('admin.')
  );

  return (
    <AuthProvider>
      <ThemeProvider>
        {/* Keep ScrollToTop outside Routes */}
        <ScrollToTop />

        <Routes>
          {/* Setup & Installation Wizard */}
          <Route path="/install" element={<InstallPage />} />
          <Route path="/admin/install" element={<InstallPage />} />

          {/* Admin Auth Routes */}
          <Route path="/admin/login" element={<Login />} />
          <Route path="/admin/forgot-password" element={<ForgotPassword />} />
          <Route path="/admin/reset-password" element={<ResetPassword />} />

          {/* Protected Admin CMS Routes under /admin */}
          <Route
            path="/admin"
            element={
              <RequireAuth>
                <AdminLayout />
              </RequireAuth>
            }
          >
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="pages" element={<PageManager />} />
            <Route path="pages/:id/builder" element={<PageBuilder />} />
            <Route path="services" element={<ServicesManager />} />
            <Route path="industries" element={<IndustriesManager />} />
            <Route path="portfolio" element={<PortfolioManager />} />
            <Route path="blog" element={<BlogManager />} />
            <Route path="media" element={<MediaLibrary />} />
            <Route path="bookings" element={<BookingsManager />} />
            <Route path="messages" element={<MessagesManager />} />
            <Route path="team" element={<TeamManager />} />
            <Route path="testimonials" element={<TestimonialsManager />} />
            <Route path="faqs" element={<FaqManager />} />
            <Route path="analytics" element={<AnalyticsDashboard />} />
            <Route path="ai-assistant" element={<AiAssistantPage />} />
            <Route path="ai" element={<AiAssistantPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="users" element={<UserManagement />} />
            <Route path="audit-logs" element={<AuditLogsPage />} />
            <Route path="audit" element={<AuditLogsPage />} />
          </Route>

          {/* Dedicated Subdomain Routing for admin.fajtech.ca */}
          {isAdminHost ? (
            <>
              <Route path="/install" element={<InstallPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route
                path="/"
                element={
                  <RequireAuth>
                    <AdminLayout />
                  </RequireAuth>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="pages" element={<PageManager />} />
                <Route path="pages/:id/builder" element={<PageBuilder />} />
                <Route path="services" element={<ServicesManager />} />
                <Route path="industries" element={<IndustriesManager />} />
                <Route path="portfolio" element={<PortfolioManager />} />
                <Route path="blog" element={<BlogManager />} />
                <Route path="media" element={<MediaLibrary />} />
                <Route path="bookings" element={<BookingsManager />} />
                <Route path="messages" element={<MessagesManager />} />
                <Route path="team" element={<TeamManager />} />
                <Route path="testimonials" element={<TestimonialsManager />} />
                <Route path="faqs" element={<FaqManager />} />
                <Route path="analytics" element={<AnalyticsDashboard />} />
                <Route path="ai-assistant" element={<AiAssistantPage />} />
                <Route path="ai" element={<AiAssistantPage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="users" element={<UserManagement />} />
                <Route path="audit-logs" element={<AuditLogsPage />} />
                <Route path="audit" element={<AuditLogsPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </>
          ) : (
            /* Public Website Layout for fajtech.ca */
            <Route path="/" element={<Layout />}>
              {/* Landing (home) */}
              <Route index element={<LandingPage />} />
              {/* Contact */}
              <Route exact path="/contact" element={<Contact />} />

              {/* Services */}
              <Route path="services" element={<ServicesPage />} />
              <Route path="services/:slug" element={<ServiceDetailPage />} />
              <Route path="services/cloud-solutions" element={<CloudSolutions />} />
              <Route path="services/managed-it-services" element={<ManagedITServices />} />
              <Route path="services/cybersecurity-services" element={<CybersecurityServices />} />
              <Route path="services/hardware-as-a-service" element={<HardwareAsAService />} />
              <Route path="services/disaster-recovery" element={<DisasterRecovery />} />
              <Route path="services/it-consulting" element={<ITConsulting />} />
              <Route path="services/vcio-services" element={<VCioServices />} />
              <Route path="services/network-security" element={<NetworkSecurity />} />
              <Route path="services/hosted-private-cloud" element={<HostedPrivateCloud />} />
              <Route path="services/managed-print-services" element={<ManagedPrintServices />} />
              <Route path="services/robotic-process-automation" element={<RoboticProcessAutomation />} />

              {/* Industries */}
              <Route path="industries" element={<IndustriesPage />} />
              <Route path="industries/:slug" element={<IndustryDetailPage />} />
              <Route path="industries/audiology" element={<Audiology />} />
              <Route path="industries/automotive" element={<Automotive />} />
              <Route path="industries/aviation" element={<Aviation />} />
              <Route path="industries/education-and-training-institutions" element={<EducationTraining />} />
              <Route path="industries/financial-services-and-accounting-firms" element={<FinancialServices />} />
              <Route path="industries/healthcare-and-medical-practices" element={<HealthcareMedical />} />
              <Route path="industries/hospitality-and-tourism" element={<HospitalityTourism />} />
              <Route path="industries/legal-firms" element={<LegalFirms />} />
              <Route path="industries/local-governments-and-municipalities" element={<LocalGovMunicipal />} />
              <Route path="industries/manufacturing-and-logistics" element={<ManufacturingLogistics />} />
              <Route path="industries/nonprofit-organizations" element={<NonprofitOrganizations />} />
              <Route path="industries/professional-services-consulting-marketing-hr" element={<ProfessionalServices />} />
              <Route path="industries/real-estate-and-property-management" element={<RealEstateProperty />} />
              <Route path="industries/retail-and-ecommerce" element={<RetailEcommerce />} />

              {/* about */}
              <Route exact path="/about" element={<About />} />
              <Route path="about/our-mission-values" element={<OurMissionAndValues />} />
              <Route path="about/awards-recognition" element={<AwardsAndRecognition />} />
              <Route path="about/e-waste-recycling" element={<EWasteRecycling />} />
              <Route path="about/events-hub" element={<EventsHub />} />
              <Route path="about/faqs" element={<FAQs />} />

              {/* Booking */}
              <Route path="book-consultation" element={<BookConsultation />} />
              <Route path="book-consultation/thanks" element={<BookConsultationThanks />} />

              {/* Team */}
              <Route path="team" element={<TeamPage />} />

              {/* Catch-all */}
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          )}
        </Routes>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
